import React from "react";
import { Chip } from "../../ds";

export const STAGE_LABEL = {
  received: "Received", ack_sent: "ACK sent", form_sent: "Form sent", customer_replied: "Customer replied",
  forwarded_to_team: "Forwarded to team", quote_sent: "Quote sent", won: "Won", lost: "Lost", spam: "Spam",
};
const LEVEL = { red: "var(--bad)", amber: "var(--warn)" };

export const dubai = (iso) => (iso ? new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Dubai", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
}).format(new Date(iso)) : "—");

export const OverdueBadge = ({ lead }) => (lead.overdue_level ? (
  <span className="font-mono text-[10.5px] uppercase px-1.5 py-0.5 rounded-[4px]" style={{ color: LEVEL[lead.overdue_level], border: `1px solid ${LEVEL[lead.overdue_level]}` }} data-testid={`afg-overdue-${lead.ref}`}>
    {lead.overdue[0]?.reason}
  </span>
) : null);

const Th = ({ cols }) => (
  <thead className="sticky top-0 bg-[color:var(--panel)] z-10">
    <tr className="text-left font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] border-b border-[color:var(--line)]">
      {cols.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}
    </tr>
  </thead>
);

const Wrap = ({ children, testId }) => (
  <div className="overflow-auto max-h-[560px] border border-[color:var(--line)]" data-testid={testId}>
    <table className="w-full text-sm">{children}</table>
  </div>
);

const Empty = ({ n, text }) => <tr><td colSpan={n} className="px-3 py-10 text-center text-[color:var(--muted)]">{text}</td></tr>;

const RefBtn = ({ refId, onOpen }) => (
  <button type="button" className="font-mono text-xs text-[color:var(--cobalt)] hover:underline focus-visible:outline-2 focus-visible:outline-[color:var(--cobalt)]" onClick={() => onOpen(refId)} data-testid={`afg-open-${refId}`}>{refId}</button>
);

export const EventsTable = ({ events, onOpen, testId }) => (
  <Wrap testId={testId}>
    <Th cols={["When (Dubai)", "Reference", "Kind", "Subject", "Preview"]} />
    <tbody>
      {events.length === 0 && <Empty n={5} text="No mails yet." />}
      {events.map((e) => (
        <tr key={e.event_id} className="border-b border-[color:var(--line)] align-top">
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{dubai(e.ts)}</td>
          <td className="px-3 py-2"><RefBtn refId={e.ref} onOpen={onOpen} /></td>
          <td className="px-3 py-2 font-mono text-xs">{e.kind.replace(/_/g, " ")}{e.day ? ` (day ${e.day})` : ""}</td>
          <td className="px-3 py-2">{e.subject}</td>
          <td className="px-3 py-2 text-[color:var(--muted)] max-w-[360px]"><p className="line-clamp-2">{e.snippet}</p></td>
        </tr>
      ))}
    </tbody>
  </Wrap>
);

export const ContactsTable = ({ contacts, onOpen }) => (
  <Wrap testId="afg-contacts-table">
    <Th cols={["Name", "Company", "Email", "Phone", "Location", "References"]} />
    <tbody>
      {contacts.length === 0 && <Empty n={6} text="No contacts yet." />}
      {contacts.map((c) => (
        <tr key={c.key} className="border-b border-[color:var(--line)]">
          <td className="px-3 py-2">{c.name}</td>
          <td className="px-3 py-2">{c.company}</td>
          <td className="px-3 py-2 font-mono text-xs">{c.email}</td>
          <td className="px-3 py-2 font-mono text-xs">{c.phone}</td>
          <td className="px-3 py-2">{c.location}</td>
          <td className="px-3 py-2 flex flex-wrap gap-2">{(c.refs || []).map((r) => <RefBtn key={r} refId={r} onOpen={onOpen} />)}</td>
        </tr>
      ))}
    </tbody>
  </Wrap>
);

export const OverdueTable = ({ leads, onOpen }) => (
  <Wrap testId="afg-overdue-table">
    <Th cols={["Reference", "Company", "Stage", "Received (Dubai)", "Alert"]} />
    <tbody>
      {leads.length === 0 && <Empty n={5} text="Nothing overdue." />}
      {leads.map((l) => (
        <tr key={l.ref} className="border-b border-[color:var(--line)]" style={{ boxShadow: `inset 3px 0 0 ${LEVEL[l.overdue_level]}` }}>
          <td className="px-3 py-2"><RefBtn refId={l.ref} onOpen={onOpen} /></td>
          <td className="px-3 py-2">{l.company || l.name}</td>
          <td className="px-3 py-2"><Chip value={STAGE_LABEL[l.stage]} /></td>
          <td className="px-3 py-2 font-mono text-xs">{dubai(l.received_at)}</td>
          <td className="px-3 py-2 flex flex-col gap-1">{l.overdue.map((o) => <span key={o.kind} className="text-xs" style={{ color: LEVEL[o.level] }}>{o.reason}</span>)}</td>
        </tr>
      ))}
    </tbody>
  </Wrap>
);

export const Pipeline = ({ leads, stages, onOpen }) => (
  <div className="flex gap-3 overflow-x-auto pb-2" data-testid="afg-pipeline">
    {stages.map((s) => {
      const col = leads.filter((l) => l.stage === s);
      return (
        <section key={s} className="min-w-[220px] w-[220px] border border-[color:var(--line)] flex flex-col max-h-[560px]" data-testid={`afg-col-${s}`}>
          <header className="px-3 py-2 border-b border-[color:var(--line)] font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] flex justify-between">
            <span>{STAGE_LABEL[s]}</span><span>{col.length}</span>
          </header>
          <div className="overflow-y-auto flex flex-col">
            {col.map((l) => (
              <button key={l.ref} type="button" onClick={() => onOpen(l.ref)} className="text-left px-3 py-2 border-b border-[color:var(--line)] hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-[color:var(--cobalt)]" style={l.overdue_level ? { boxShadow: `inset 3px 0 0 ${LEVEL[l.overdue_level]}` } : undefined} data-testid={`afg-card-${l.ref}`}>
                <span className="block font-mono text-xs text-[color:var(--cobalt)]">{l.ref}</span>
                <span className="block text-sm">{l.company || l.name || "—"}</span>
                <span className="block text-xs text-[color:var(--muted)]">{[l.product, l.capacity_m3_day ? `${l.capacity_m3_day} m³/day` : null].filter(Boolean).join(" · ")}</span>
                <OverdueBadge lead={l} />
              </button>
            ))}
          </div>
        </section>
      );
    })}
  </div>
);
